'use client';

import { c as _c } from "react/compiler-runtime";
import { SvgChevronright, SvgWarningFill } from '@common/icon';
import { cn } from '@core/utils';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { CustomLink } from '@/app/_components/CustomLink';
import { Rating } from '@/app/donation/_components/Rating';
import { ShareButton } from '@/app/donation/_components/ShareButton';
import { GIFT_SALE_STATUS } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScrollToSection } from '@/hooks/useScrollToSection';
import { DELIVERY_PERIOD_LABEL } from '../../../constants';
import { PriceInfo } from '../../GiftDetailHeader/_components/PriceInfo';
import { PrefillForm } from '../PrefillForm';
import { DesktopMainImages } from './DesktopMainImages';
import { MobileHero } from './MobileHero';
interface HeaderInfoSectionProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionId: number;
  institutionLogoUrl: string;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  requiredDonationAmount: number;
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  productData?: GiftProductDetail;
  className?: string;
}
export const HeaderInfoSection = t0 => {
  const $ = _c(66);
  const {
    title,
    heroImageUrlList,
    adultYN,
    institutionId,
    institutionLogoUrl,
    institutionName,
    productSaleStatus,
    productName,
    reviewCount,
    reviewRating,
    productNumber,
    requiredDonationAmount,
    exchangeYN,
    onlineProductYN,
    onlineProductDesc,
    productData,
    className
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const {
    scrollToSectionWithTab
  } = useScrollToSection();
  const isStoppedSale = productSaleStatus === GIFT_SALE_STATUS.STOPPED;
  let t1;
  if ($[0] !== className) {
    t1 = cn("grid grid-cols-2 gap-28", "tablet:grid-cols-1", "mobile:grid-cols-1", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== adultYN || $[3] !== heroImageUrlList) {
    t2 = <MobileHero heroImageUrlList={heroImageUrlList} adultYN={adultYN} />;
    $[2] = adultYN;
    $[3] = heroImageUrlList;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== adultYN || $[6] !== heroImageUrlList || $[7] !== title) {
    t3 = <DesktopMainImages title={title} heroImageUrlList={heroImageUrlList} adultYN={adultYN} />;
    $[5] = adultYN;
    $[6] = heroImageUrlList;
    $[7] = title;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== institutionId || $[10] !== productData?.institutionSlugId) {
    t4 = DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
      id: productData?.institutionSlugId ?? institutionId
    });
    $[9] = institutionId;
    $[10] = productData?.institutionSlugId;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== institutionLogoUrl || $[13] !== institutionName) {
    t5 = <div className="relative size-28 shrink-0 overflow-hidden rounded-pill border border-Gray-300"><Image fill={true} src={institutionLogoUrl} alt={institutionName} className="object-cover object-center" loading="eager" sizes="28px" /></div>;
    $[12] = institutionLogoUrl;
    $[13] = institutionName;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== institutionName) {
    t6 = <p className="font-600 text-Gray-600 typo-body4">{institutionName}</p>;
    $[15] = institutionName;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SvgChevronright className="size-16 text-Gray-600" />;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t8 = <CustomLink href={t4} className="flex items-center gap-4">{t5}{t6}{t7}</CustomLink>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ShareButton />;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8) {
    t10 = <div className="mb-8 flex items-center justify-between gap-4">{t8}{t9}</div>;
    $[23] = t8;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== isStoppedSale) {
    t11 = cn("flex-1 break-all font-700 text-Gray-900 typo-h3", "tablet:font-600 tablet:typo-body2", "mobile:font-600 mobile:typo-body2", {
      "text-Gray-400": isStoppedSale
    });
    $[25] = isStoppedSale;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== productName || $[28] !== t11) {
    t12 = <div className="mb-8 flex justify-between gap-16"><h1 className={t11}>{productName}</h1></div>;
    $[27] = productName;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn("mb-28 flex gap-8", "tablet:gap-4", "mobile:gap-4");
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = reviewRating ?? 0;
  const t15 = isDesktopViewport ? 24 : 12;
  let t16;
  if ($[31] !== t14 || $[32] !== t15) {
    t16 = <Rating value={t14} size={t15} />;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== reviewCount || $[35] !== scrollToSectionWithTab) {
    t17 = reviewCount > 0 && <button type="button" onClick={() => scrollToSectionWithTab("review")} className={cn("font-600 text-Gray-600 underline typo-body3", "tablet:typo-caption1", "mobile:typo-caption1")}>{reviewCount.toLocaleString()}건 후기 보기</button>;
    $[34] = reviewCount;
    $[35] = scrollToSectionWithTab;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t16 || $[38] !== t17) {
    t18 = <div className={t13}>{t16}{t17}</div>;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== requiredDonationAmount) {
    t19 = <div className="mb-20 flex items-end justify-between"><PriceInfo amount={requiredDonationAmount} /></div>;
    $[40] = requiredDonationAmount;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = cn("flex flex-col gap-24 border-t border-Gray-200 py-20", "tablet:gap-16", "mobile:gap-16");
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== onlineProductDesc || $[44] !== onlineProductYN) {
    t21 = onlineProductYN ? <div className={cn("flex flex-col gap-12 typo-body3", "tablet:typo-body4", "mobile:typo-body4")}><div className="flex items-start gap-16"><p className="w-92 shrink-0 font-400 text-Gray-600">배송정보</p><p className="font-700 text-Gray-700">온라인 답례품</p></div>{onlineProductDesc ? <div className="flex items-start gap-16"><p className="w-92 shrink-0 font-400 text-Gray-600">답례품 설명</p><p className="whitespace-pre-line font-700 text-Gray-700">{onlineProductDesc}</p></div> : null}</div> : <div className={cn("flex flex-col gap-12 typo-body3", "tablet:typo-body4", "mobile:typo-body4")}><div className="flex items-center gap-16"><p className="w-92 font-400 text-Gray-600">배송정보</p><p className="font-700 text-Gray-700">무료배송</p></div><div className="flex gap-16"><p className="w-92 shrink-0 font-400 text-Gray-600">배송안내</p><div className="flex flex-col gap-4"><p className="font-700 text-Gray-700">{DELIVERY_PERIOD_LABEL}</p></div></div></div>;
    $[43] = onlineProductDesc;
    $[44] = onlineProductYN;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== exchangeYN) {
    t22 = !exchangeYN && <div className="gap-8 rounded-xsmall bg-Gray-50 px-20 py-12 font-400 text-Gray-600 flex-center typo-caption1"><SvgWarningFill className="size-16 text-B-500" /><p>해당 답례품은 반품/교환이 불가능합니다.</p></div>;
    $[46] = exchangeYN;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t21 || $[49] !== t22) {
    t23 = <div className={t20}>{t21}{t22}</div>;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== productData || $[52] !== productNumber) {
    t24 = <PrefillForm productData={productData} productNumber={productNumber} />;
    $[51] = productData;
    $[52] = productNumber;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t10 || $[55] !== t12 || $[56] !== t18 || $[57] !== t19 || $[58] !== t23 || $[59] !== t24) {
    t25 = <div><section className="flex w-full flex-col px-20">{t10}{t12}{t18}{t19}{t23}{t24}</section></div>;
    $[54] = t10;
    $[55] = t12;
    $[56] = t18;
    $[57] = t19;
    $[58] = t23;
    $[59] = t24;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t1 || $[62] !== t2 || $[63] !== t25 || $[64] !== t3) {
    t26 = <div className={t1}>{t2}{t3}{t25}</div>;
    $[61] = t1;
    $[62] = t2;
    $[63] = t25;
    $[64] = t3;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  return t26;
};